import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Typography,
  Card,
  Grid,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  ChevronLeft,
  CheckCircle2,
  Server,
  Layers,
  Phone,
  Wifi,
  Tv,
  Zap as ElectricityIcon,
  GraduationCap,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import { getServices, getServiceProviders } from '../api/services';
import { BRAND_IMAGES } from '../utils/assets';

// Robust brand logo finder
const getBrandLogo = (category, details) => {
  const text = `${category || ''} ${details?.name || ''} ${details?.planName || ''}`
    .toLowerCase()
    .replace(/[\s\-_]/g, '');
  const matchedKey = Object.keys(BRAND_IMAGES).find((key) => text.includes(key));
  return matchedKey ? BRAND_IMAGES[matchedKey] : null;
};

const NETWORK_COLORS = {
  MTN: '#EAB308',
  AIRTEL: '#EF4444',
  GLO: '#22C55E',
  '9MOBILE': '#15803D',
};

const getCategoryIcon = (serviceType) => {
  switch ((serviceType || '').toLowerCase()) {
    case 'data':
      return Wifi;
    case 'airtime':
      return Phone;
    case 'cable':
      return Tv;
    case 'electricity':
      return ElectricityIcon;
    case 'resultcheck':
      return GraduationCap;
    default:
      return Layers;
  }
};

export default function ProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [srvRes, provRes] = await Promise.all([getServices(), getServiceProviders()]);
      if (srvRes.ok && srvRes.data?.data) {
        const found = srvRes.data.data.find((s) => String(s.id) === String(id));
        if (found) {
          setProduct(found);
        } else {
          toast.error(`Product #${id} not found.`);
        }
      }
      if (provRes.ok && provRes.data?.data) {
        setProviders(provRes.data.data);
      }
    } catch (err) {
      toast.error('Error loading product details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const providerMap = useMemo(() => {
    const map = {};
    providers.forEach((p) => {
      map[p.slug] = p;
    });
    return map;
  }, [providers]);

  if (loading) {
    return (
      <Page title="Product Details | Dashboard">
        <Container maxWidth="md" sx={{ py: 12 }}>
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  if (!product) {
    return (
      <Page title="Product Details | Dashboard">
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Button
            startIcon={<ChevronLeft size={18} />}
            onClick={() => navigate('/dashboard/services')}
            sx={{ mb: 3, textTransform: 'none', color: '#0066FE', fontWeight: 600 }}
          >
            Airtime and data
          </Button>
          <Card sx={{ p: 5, textAlign: 'center', borderRadius: '24px', border: '1px solid #ECEEF1' }}>
            <Typography variant="h6" color="text.secondary">
              Product ID #{id} was not found.
            </Typography>
          </Card>
        </Container>
      </Page>
    );
  }

  const isAirtime = (product.service || '').toLowerCase() === 'airtime';
  const brandLogo = getBrandLogo(product.category, product.details);
  const CategoryIcon = getCategoryIcon(product.service);

  const discountNum = Number(product.discount || 0);
  const buyingNum = Number(product.buyingAmount || 0);
  const marginNum = buyingNum - discountNum;

  const retailAmount = Number(product.details?.amount || product.amount || 0);
  const retailBuyingAmount = Number(product.buyingAmount || 0);
  const retailCashback = Number(product.cashback || 0);
  const retailReferralBonus = Number(product.referralBonus || 0);
  const totalRewards = retailCashback + retailReferralBonus;
  const retailMargin = retailAmount - retailBuyingAmount - totalRewards;

  const routes = product.processorRoutes || [];

  // Determine back navigation label
  const backLabel =
    isAirtime || (product.service || '').toLowerCase() === 'data'
      ? 'Airtime and data'
      : `${product.service || 'Services'}`;

  // Determine clean product display title
  let productTitle = isAirtime
    ? `${product.category} Airtime VTU`
    : product.details?.name || product.details?.planName || `${product.category} Plan`;

  const isData = (product.service || '').toLowerCase() === 'data';
  const detectNet = () => {
    const text = `${product.category || ''} ${product.details?.name || ''}`.toUpperCase();
    if (text.includes('MTN')) return 'MTN';
    if (text.includes('AIRTEL')) return 'AIRTEL';
    if (text.includes('GLO')) return 'GLO';
    if (text.includes('9MOBILE')) return '9MOBILE';
    return null;
  };
  const networkName = detectNet();
  if (isData && networkName && !productTitle.toUpperCase().includes(networkName)) {
    productTitle = `${networkName} ${productTitle}`;
  }

  const validityText = product.validity || (isAirtime ? 'Instant' : 'Monthly');

  let routingSubtitle = 'Orders use the legacy single-processor engine.';
  if (product.useV2Engine) {
    if (routes.length > 0) {
      routingSubtitle = `${routes.length} configured failover route${routes.length === 1 ? '' : 's'} with auto retry.`;
    } else {
      routingSubtitle = 'V2 router enabled. Configure fallback routes for failover protection.';
    }
  }

  return (
    <Page title={`${productTitle} | Product Details`}>
      <Container maxWidth="md" sx={{ py: 3, maxWidth: 880, mx: 'auto' }}>
        {/* Top Bar: Back Link & Edit Button */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3.5}>
          <Box
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate(`/dashboard/services/category/${(product.service || 'data').toLowerCase()}`);
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#0066FE',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
            <span>{backLabel}</span>
          </Box>

          <Button
            onClick={() => navigate(`/dashboard/services/edit/${product.id}`)}
            sx={{
              borderRadius: '24px',
              bgcolor: '#0066FE',
              color: '#FFFFFF',
              px: 3,
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#0052CC',
                boxShadow: 'none',
              },
            }}
          >
            Edit product
          </Button>
        </Stack>

        {/* Hero Section: Brand Logo & Product Info */}
        <Stack direction="row" spacing={3} alignItems="center" mb={4}>
          {brandLogo ? (
            <Box
              component="img"
              src={brandLogo}
              alt={product.category}
              sx={{
                width: { xs: 80, sm: 96 },
                height: { xs: 80, sm: 96 },
                borderRadius: '24px',
                objectFit: 'cover',
                flexShrink: 0,
                boxShadow: '0 8px 24px -4px rgba(0,0,0,0.1)',
                border: '1px solid #ECEEF1',
              }}
            />
          ) : (
            <Box
              sx={{
                width: { xs: 80, sm: 96 },
                height: { xs: 80, sm: 96 },
                borderRadius: '24px',
                bgcolor: '#EFF6FF',
                color: '#0066FE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid #DBEAFE',
              }}
            >
              <CategoryIcon size={42} />
            </Box>
          )}

          <div>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.85rem', sm: '2.5rem' },
                color: '#111827',
                lineHeight: 1.15,
                mb: 0.75,
              }}
            >
              {productTitle}
            </Typography>

            <Typography
              sx={{
                color: '#6B7280',
                fontSize: { xs: '0.875rem', sm: '0.95rem' },
                fontWeight: 500,
                mb: 1.75,
              }}
            >
              {product.service} · {product.category} · Service #{product.id} · {validityText}
            </Typography>

            {/* Badges Row */}
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              {/* Telecom Network Badge (Data) */}
              {isData && networkName && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.8,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '16px',
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    color: '#111827',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: NETWORK_COLORS[networkName] || '#0066FE',
                    }}
                  />
                  <span>{networkName}</span>
                </Box>
              )}

              {/* Availability Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '16px',
                  bgcolor: product.available ? '#DCFCE7' : '#FEE2E2',
                  color: product.available ? '#16A34A' : '#DC2626',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: product.available ? '#16A34A' : '#DC2626',
                  }}
                />
                <span>{product.available ? 'Available' : 'Unavailable'}</span>
              </Box>

              {/* Visibility Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '16px',
                  bgcolor: !product.hidden ? '#DCFCE7' : '#F3F4F6',
                  color: !product.hidden ? '#16A34A' : '#6B7280',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: !product.hidden ? '#16A34A' : '#9CA3AF',
                  }}
                />
                <span>{!product.hidden ? 'On mobile app' : 'Hidden from app'}</span>
              </Box>

              {/* V2 Engine Mode Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '16px',
                  bgcolor: product.useV2Engine ? '#DCFCE7' : '#F3F4F6',
                  color: product.useV2Engine ? '#16A34A' : '#6B7280',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {product.useV2Engine ? `V2 active (${routes.length})` : 'Legacy mode'}
              </Box>
            </Stack>
          </div>
        </Stack>

        {/* Financial Metrics 3-Column Card */}
        <Card
          sx={{
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECEEF1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            overflow: 'hidden',
            mb: 4,
          }}
        >
          {isAirtime ? (
            <Grid container>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  borderRight: { md: '1px solid #F1F3F5' },
                  borderBottom: { xs: '1px solid #F1F3F5', md: 'none' },
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                  Retail discount
                </Typography>
                <Typography
                  sx={{
                    color: '#0066FE',
                    fontSize: { xs: '1.8rem', sm: '2.15rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {discountNum.toFixed(2)}%
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>
                  Customer airtime discount
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  borderRight: { md: '1px solid #F1F3F5' },
                  borderBottom: { xs: '1px solid #F1F3F5', md: 'none' },
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                  Vendor cost
                </Typography>
                <Typography
                  sx={{
                    color: '#111827',
                    fontSize: { xs: '1.8rem', sm: '2.15rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {buyingNum.toFixed(2)}%
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>
                  Wholesale provider rate
                </Typography>
              </Grid>

              <Grid item xs={12} md={4} sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                  Net profit
                </Typography>
                <Typography
                  sx={{
                    color: marginNum >= 0 ? '#16A34A' : '#DC2626',
                    fontSize: { xs: '1.8rem', sm: '2.15rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {marginNum >= 0 ? `+${marginNum.toFixed(2)}%` : `${marginNum.toFixed(2)}%`}
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>
                  Per transaction
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  borderRight: { md: '1px solid #F1F3F5' },
                  borderBottom: { xs: '1px solid #F1F3F5', md: 'none' },
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                  Retail price
                </Typography>
                <Typography
                  sx={{
                    color: '#0066FE',
                    fontSize: { xs: '1.8rem', sm: '2.15rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  ₦{retailAmount.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>
                  VIP price ₦{Number(product.vipAmount || 0).toLocaleString()}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  p: { xs: 2.5, sm: 3.5 },
                  borderRight: { md: '1px solid #F1F3F5' },
                  borderBottom: { xs: '1px solid #F1F3F5', md: 'none' },
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                  Vendor cost
                </Typography>
                <Typography
                  sx={{
                    color: '#111827',
                    fontSize: { xs: '1.8rem', sm: '2.15rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  ₦{retailBuyingAmount.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>
                  Paid to vendor
                </Typography>
              </Grid>

              <Grid item xs={12} md={4} sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                  Net profit
                </Typography>
                <Typography
                  sx={{
                    color: retailMargin >= 0 ? '#16A34A' : '#DC2626',
                    fontSize: { xs: '1.8rem', sm: '2.15rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {retailMargin >= 0 ? `+₦${retailMargin.toLocaleString()}` : `-₦${Math.abs(retailMargin).toLocaleString()}`}
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>
                  {totalRewards > 0 ? `After ₦${totalRewards.toLocaleString()} rewards` : 'Per transaction'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Card>

        {/* Section: Routing */}
        <Box mb={4}>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#111827', mb: 2 }}>
            Routing
          </Typography>

          <Card
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1px solid #ECEEF1',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              mb: product.useV2Engine && routes.length > 0 ? 2 : 0,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={2}
            >
              <Stack direction="row" alignItems="center" spacing={2.5}>
                {product.useV2Engine ? (
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '16px',
                      bgcolor: '#DCFCE7',
                      color: '#16A34A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircle2 size={24} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '16px',
                      bgcolor: '#FEF9C3',
                      color: '#CA8A04',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontWeight: 800,
                      fontSize: '1.35rem',
                      fontFamily: 'system-ui, sans-serif',
                    }}
                  >
                    !
                  </Box>
                )}

                <div>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.025rem', color: '#111827', mb: 0.25 }}>
                    {product.useV2Engine ? 'V2 routing is active' : 'V2 routing is off'}
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                    {routingSubtitle}
                  </Typography>
                </div>
              </Stack>

              <Button
                onClick={() => navigate(`/dashboard/services/edit/${product.id}`)}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#F1F3F5',
                  color: '#374151',
                  px: 2.75,
                  py: 1,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  '&:hover': {
                    bgcolor: '#E5E7EB',
                    boxShadow: 'none',
                  },
                }}
              >
                Configure routes
              </Button>
            </Box>
          </Card>

          {/* If V2 is active and has routes, list them cleanly */}
          {product.useV2Engine && routes.length > 0 && (
            <Card
              sx={{
                borderRadius: '24px',
                bgcolor: '#FFFFFF',
                border: '1px solid #ECEEF1',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                overflow: 'hidden',
              }}
            >
              {routes.map((route, idx) => {
                const provInfo = providerMap[route.processorSlug];
                const isLast = idx === routes.length - 1;
                return (
                  <Box
                    key={idx}
                    sx={{
                      p: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: isLast ? 'none' : '1px solid #F3F4F6',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.4,
                          borderRadius: '12px',
                          bgcolor: idx === 0 ? '#EFF6FF' : '#F3F4F6',
                          color: idx === 0 ? '#2563EB' : '#4B5563',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}
                      >
                        Priority {route.priority || idx + 1}
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Server size={18} color="#9CA3AF" />
                        <div>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.925rem', color: '#111827' }}>
                            {provInfo?.name || route.processorSlug}
                          </Typography>
                          <Typography sx={{ color: '#6B7280', fontSize: '0.775rem' }}>
                            Vendor Plan ID:{' '}
                            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {route.planId || 'Default'}
                            </span>
                          </Typography>
                        </div>
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.4,
                        borderRadius: '12px',
                        bgcolor: route.retryOnFailure ? '#DCFCE7' : '#F3F4F6',
                        color: route.retryOnFailure ? '#16A34A' : '#6B7280',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {route.retryOnFailure ? 'Failover retry on' : 'No retry'}
                    </Box>
                  </Box>
                );
              })}
            </Card>
          )}
        </Box>

        {/* Section: Configuration */}
        <Box mb={6}>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#111827', mb: 2 }}>
            Configuration
          </Typography>

          <Card
            sx={{
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1px solid #ECEEF1',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              overflow: 'hidden',
            }}
          >
            {[
              { label: 'Category', value: product.service },
              { label: 'Network', value: product.category },
              { label: 'Validity', value: validityText },
              { label: 'Service fee', value: `${Number(product.fee || 0).toFixed(2)}%` },
              { label: 'Cashback', value: `₦${Number(product.cashback || 0).toLocaleString()}` },
              { label: 'Referral bonus', value: `₦${Number(product.referralBonus || 0).toLocaleString()}` },
            ].map((row, idx, arr) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: { xs: 2.5, sm: 3.5 },
                  py: 2.2,
                  borderBottom: idx === arr.length - 1 ? 'none' : '1px solid #F3F4F6',
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '0.925rem', fontWeight: 500 }}>
                  {row.label}
                </Typography>
                <Typography sx={{ color: '#111827', fontSize: '0.925rem', fontWeight: 600 }}>
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Card>
        </Box>
      </Container>
    </Page>
  );
}
